import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  lerCnaes,
  lerNumeros,
  mensagemDoAdmin,
  salvarCnae,
  salvarFaixa,
  salvarParametro,
  type CnaeAdmin,
  type FaixaAdmin,
  type PaginaDeCnaes,
  type ParametroAdmin,
} from "../lib/radarAdmin";
import { NOME_DO_ANEXO, ROMANO, emPercentual, emReais, ordinalDaFaixa } from "../lib/simplesNacional";

/**
 * Admin › Radar Simples.
 *
 * Três coisas que só a equipe mantém, e que ficaram sem tela quando o Radar
 * entrou no ar (000184-000187):
 *
 *   CNAEs      — o catálogo, que desde a 000189 é UM só para todas as
 *                ferramentas. Mexer aqui mexe também na lista de ocupações do
 *                MEI, e a tela DIZ isso em cada linha que tem ocupação apontando.
 *   Parâmetros — CBS e IBS, que hoje são estimativa do PLP 68/2024.
 *   Faixas     — as 30 dos cinco anexos, que mudam por lei complementar.
 *
 * A sub-aba viaja na QUERY (`?aba=radar&sec=faixas`) e não em `useState`: o
 * operador precisa poder mandar o link de um painel a um colega, e recarregar
 * não pode jogá-lo de volta na primeira. É a mesma razão de a aba de ferramenta
 * ser sub-rota.
 */

const SECOES = [
  { id: "cnaes", rotulo: "CNAEs" },
  { id: "parametros", rotulo: "Parâmetros" },
  { id: "faixas", rotulo: "Faixas dos anexos" },
] as const;

const GAVETAS = [
  { id: "todos", rotulo: "Todos" },
  { id: "revisar", rotulo: "A revisar" },
  { id: "com_nota", rotulo: "Com nota de origem" },
  { id: "conferidos", rotulo: "Já conferidos" },
  { id: "desligados", rotulo: "Desligados" },
] as const;

const POR_PAGINA = 25;

export default function AdminRadar() {
  const [params, setParams] = useSearchParams();
  const sec = params.get("sec") ?? "cnaes";

  return (
    <div className="space-y-5">
      <nav className="flex flex-wrap gap-2" aria-label="Seções do Radar Simples">
        {SECOES.map((s) => (
          <button
            key={s.id}
            type="button"
            data-sec={s.id}
            aria-current={sec === s.id ? "page" : undefined}
            onClick={() => {
              const p = new URLSearchParams(params);
              p.set("sec", s.id);
              setParams(p, { replace: true });
            }}
            className={`rounded-full px-4 py-2 text-sm font-medium transition ${
              sec === s.id
                ? "bg-acento text-acento-tinta"
                : "border border-borda bg-superficie text-tinta hover:border-acento/40"
            }`}
          >
            {s.rotulo}
          </button>
        ))}
      </nav>

      {sec === "cnaes" && <SecaoCnaes />}
      {sec === "parametros" && <SecaoNumeros mostrar="parametros" />}
      {sec === "faixas" && <SecaoNumeros mostrar="faixas" />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CNAEs
// ---------------------------------------------------------------------------

function SecaoCnaes() {
  const [termo, setTermo] = useState("");
  const [busca, setBusca] = useState("");
  const [gaveta, setGaveta] = useState<string>("todos");
  const [pagina, setPagina] = useState(0);
  const [dados, setDados] = useState<PaginaDeCnaes | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [aberto, setAberto] = useState<string | null>(null);

  // O termo digitado só vira busca depois de uma pausa. Sem isto, cada tecla é
  // uma ida ao servidor sobre 1.333 linhas — e a resposta da penúltima pode
  // chegar depois da última, mostrando o resultado errado.
  useEffect(() => {
    const t = setTimeout(() => {
      setBusca(termo);
      setPagina(0);
    }, 350);
    return () => clearTimeout(t);
  }, [termo]);

  const buscar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await lerCnaes({
        termo: busca,
        situacao: gaveta,
        limite: POR_PAGINA,
        offset: pagina * POR_PAGINA,
      });
      setDados(r);
      setErro(null);
    } catch (e) {
      setErro(e instanceof Error ? e.message : "não foi possível ler o catálogo");
    } finally {
      setCarregando(false);
    }
  }, [busca, gaveta, pagina]);

  useEffect(() => {
    void buscar();
  }, [buscar]);

  const resumo = dados?.resumo;
  const paginas = dados ? Math.ceil(dados.total / POR_PAGINA) : 0;

  return (
    <div className="space-y-4">
      <section className="rounded-cf border border-borda bg-superficie p-4">
        <h3 className="text-sm font-semibold text-titulo">O catálogo de CNAEs</h3>
        <p className="mt-1 text-sm text-cinza">
          Desde a migration 000189 esta lista é <strong>uma só</strong> para todo o hub: o Radar
          Simples enquadra por ela, e as ocupações do MEI apontam para ela. Antes eram duas listas,
          com 37 descrições divergentes para o mesmo código.
        </p>

        {resumo && (
          <div className="mt-3 grid gap-2 sm:grid-cols-5">
            <Contador rotulo="No catálogo" valor={resumo.total} />
            <Contador rotulo="A revisar" valor={resumo.a_revisar} destaque={resumo.a_revisar > 0} />
            <Contador rotulo="Com nota" valor={resumo.com_nota} destaque={resumo.com_nota > 0} />
            <Contador rotulo="Conferidos" valor={resumo.conferidos} />
            <Contador rotulo="Desligados" valor={resumo.desligados} />
          </div>
        )}
      </section>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[16rem] flex-1 space-y-1.5">
          <label htmlFor="busca-cnae" className="block text-sm font-medium text-tinta">
            Buscar por código ou descrição
          </label>
          <input
            id="busca-cnae"
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            placeholder="6201, consultoria, padaria…"
            className="w-full rounded-cf border border-borda bg-superficie px-3 py-2 text-tinta outline-none focus:border-acento focus:ring-2 focus:ring-acento/30"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {GAVETAS.map((g) => (
            <button
              key={g.id}
              type="button"
              data-gaveta={g.id}
              onClick={() => {
                setGaveta(g.id);
                setPagina(0);
              }}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                gaveta === g.id
                  ? "bg-acento text-acento-tinta"
                  : "border border-borda bg-superficie text-cinza hover:border-acento/40"
              }`}
            >
              {g.rotulo}
            </button>
          ))}
        </div>
      </div>

      {erro && (
        <p className="rounded-cf border border-erro-borda bg-erro-fundo px-3 py-2 text-sm text-erro-tinta">
          {erro}
        </p>
      )}

      {carregando && !dados ? (
        <p className="text-sm text-cinza">Carregando…</p>
      ) : dados && dados.cnaes.length === 0 ? (
        <p className="rounded-cf border border-dashed border-borda px-3 py-6 text-center text-sm text-cinza">
          Nenhum CNAE nesta busca.
        </p>
      ) : (
        <>
          <p className="text-sm text-cinza">
            {dados?.total.toLocaleString("pt-BR")}{" "}
            {dados?.total === 1 ? "atividade" : "atividades"}
            {busca && ` para “${busca}”`}
            {paginas > 1 && ` · página ${pagina + 1} de ${paginas}`}
          </p>

          <ul className="space-y-2">
            {dados?.cnaes.map((c) => (
              <li key={c.codigo}>
                <LinhaDeCnae
                  cnae={c}
                  aberto={aberto === c.codigo}
                  aoAlternar={() => setAberto((a) => (a === c.codigo ? null : c.codigo))}
                  aoSalvar={buscar}
                />
              </li>
            ))}
          </ul>

          {paginas > 1 && (
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                disabled={pagina === 0}
                onClick={() => setPagina((p) => Math.max(0, p - 1))}
                className="rounded-full border border-borda px-4 py-2 text-sm text-tinta disabled:opacity-50"
              >
                ← Anteriores
              </button>
              <button
                type="button"
                disabled={pagina + 1 >= paginas}
                onClick={() => setPagina((p) => p + 1)}
                className="rounded-full border border-borda px-4 py-2 text-sm text-tinta disabled:opacity-50"
              >
                Próximos →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Contador({
  rotulo,
  valor,
  destaque,
}: {
  rotulo: string;
  valor: number;
  destaque?: boolean;
}) {
  return (
    <div
      className={`rounded-cf border px-3 py-2 ${
        destaque ? "border-alerta-tinta/40 bg-alerta-fundo" : "border-borda bg-superficie"
      }`}
    >
      <p className={`text-[11px] uppercase tracking-[0.1em] ${destaque ? "text-alerta-tinta" : "text-cinza"}`}>
        {rotulo}
      </p>
      <p className={`text-lg font-bold tabular-nums ${destaque ? "text-alerta-tinta" : "text-titulo"}`}>
        {valor.toLocaleString("pt-BR")}
      </p>
    </div>
  );
}

function LinhaDeCnae({
  cnae,
  aberto,
  aoAlternar,
  aoSalvar,
}: {
  cnae: CnaeAdmin;
  aberto: boolean;
  aoAlternar: () => void;
  aoSalvar: () => void | Promise<void>;
}) {
  const [descricao, setDescricao] = useState(cnae.descricao);
  const [permitido, setPermitido] = useState(cnae.permitido_simples);
  const [anexo, setAnexo] = useState<number | null>(cnae.anexo_principal);
  const [fatorR, setFatorR] = useState(cnae.exige_fator_r);
  const [classificacao, setClassificacao] = useState(cnae.classificacao ?? "");
  const [ressalva, setRessalva] = useState(cnae.ressalva ?? "");
  const [nota, setNota] = useState(cnae.nota_de_origem ?? "");
  const [ativo, setAtivo] = useState(cnae.ativo);
  const [salvando, setSalvando] = useState(false);
  const [recado, setRecado] = useState<{ bom: boolean; texto: string } | null>(null);

  async function gravar(confirmar: boolean) {
    setSalvando(true);
    setRecado(null);
    const r = await salvarCnae({
      codigo: cnae.codigo,
      descricao,
      permitido_simples: permitido,
      anexo_principal: permitido === "Não" ? null : anexo,
      exige_fator_r: fatorR,
      classificacao,
      ressalva,
      nota_de_origem: nota,
      ativo,
      confirmar,
    });
    setSalvando(false);
    if (r.ok) {
      setRecado({ bom: true, texto: confirmar ? "Salvo e confirmado." : "Salvo." });
      await aoSalvar();
    } else {
      setRecado({ bom: false, texto: mensagemDoAdmin(r) });
    }
  }

  return (
    <div
      data-codigo={cnae.codigo}
      className={`rounded-cf border bg-superficie ${
        cnae.permitido_simples === "Revisar" ? "border-alerta-tinta/40" : "border-borda"
      }`}
    >
      <button
        type="button"
        onClick={aoAlternar}
        aria-expanded={aberto}
        className="flex w-full flex-wrap items-center gap-2 px-4 py-3 text-left"
      >
        <span className="font-mono font-semibold text-titulo">{cnae.codigo}</span>
        <span className="min-w-0 flex-1 truncate text-sm text-tinta">{cnae.descricao}</span>
        {!cnae.ativo && (
          <span className="rounded-full border border-borda px-2 py-0.5 text-[11px] text-cinza">
            desligado
          </span>
        )}
        {cnae.permitido_simples === "Revisar" && (
          <span className="rounded-full border border-alerta-tinta/40 bg-alerta-fundo px-2 py-0.5 text-[11px] font-medium text-alerta-tinta">
            a revisar
          </span>
        )}
        {cnae.anexo_principal && (
          <span className="rounded-full border border-acento/30 bg-acento/12 px-2 py-0.5 text-[11px] font-medium text-acento">
            Anexo {ROMANO[cnae.anexo_principal]}
          </span>
        )}
        {/* O que diz à equipe que mexer aqui mexe na outra ferramenta. Sem isto,
            a lista única é uma decisão invisível para quem edita. */}
        {cnae.ocupacoes_mei > 0 && (
          <span className="rounded-full border border-borda px-2 py-0.5 text-[11px] text-cinza">
            {cnae.ocupacoes_mei} {cnae.ocupacoes_mei === 1 ? "ocupação MEI" : "ocupações MEI"}
          </span>
        )}
        <span aria-hidden className="text-cinza">
          {aberto ? "▴" : "▾"}
        </span>
      </button>

      {aberto && (
        <div className="space-y-4 border-t border-borda px-4 py-4">
          {cnae.nota_de_origem && (
            <div className="rounded-cf border border-alerta-tinta/40 bg-alerta-fundo px-3 py-2 text-xs text-alerta-tinta">
              <strong className="block">Nota de origem</strong>
              {cnae.nota_de_origem}
            </div>
          )}

          {cnae.ocupacoes_mei > 0 && (
            <p className="text-xs text-cinza">
              Este código é usado por <strong>{cnae.ocupacoes_mei}</strong>{" "}
              {cnae.ocupacoes_mei === 1 ? "ocupação" : "ocupações"} do MEI. A descrição abaixo é a
              que aparece nas duas ferramentas.
            </p>
          )}

          <div className="space-y-1.5">
            <label htmlFor={`desc-${cnae.codigo}`} className="block text-sm font-medium text-tinta">
              Descrição
            </label>
            <input
              id={`desc-${cnae.codigo}`}
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              className="w-full rounded-cf border border-borda bg-superficie px-3 py-2 text-tinta outline-none focus:border-acento focus:ring-2 focus:ring-acento/30"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label htmlFor={`perm-${cnae.codigo}`} className="block text-sm font-medium text-tinta">
                Permitido no Simples
              </label>
              <select
                id={`perm-${cnae.codigo}`}
                value={permitido}
                onChange={(e) => setPermitido(e.target.value as CnaeAdmin["permitido_simples"])}
                className="w-full rounded-cf border border-borda bg-superficie px-3 py-2 text-tinta outline-none focus:border-acento"
              >
                <option value="Sim">Sim</option>
                <option value="Não">Não</option>
                <option value="Revisar">Revisar</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label htmlFor={`anexo-${cnae.codigo}`} className="block text-sm font-medium text-tinta">
                Anexo principal
              </label>
              <select
                id={`anexo-${cnae.codigo}`}
                value={anexo ?? ""}
                disabled={permitido === "Não"}
                onChange={(e) => setAnexo(e.target.value ? Number(e.target.value) : null)}
                className="w-full rounded-cf border border-borda bg-superficie px-3 py-2 text-tinta outline-none focus:border-acento disabled:opacity-50"
              >
                <option value="">Sem anexo</option>
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>
                    {NOME_DO_ANEXO[n]}
                  </option>
                ))}
              </select>
              {permitido === "Não" && (
                <p className="text-xs text-cinza">CNAE vedado ao Simples não tem anexo.</p>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <label htmlFor={`class-${cnae.codigo}`} className="block text-sm font-medium text-tinta">
              Classificação da atividade
            </label>
            <input
              id={`class-${cnae.codigo}`}
              value={classificacao}
              onChange={(e) => setClassificacao(e.target.value)}
              className="w-full rounded-cf border border-borda bg-superficie px-3 py-2 text-tinta outline-none focus:border-acento focus:ring-2 focus:ring-acento/30"
            />
            <p className="text-xs text-cinza">
              O que a atividade É. Aparece como texto neutro na tela do aluno — não como aviso.
            </p>
          </div>

          <div className="space-y-1.5">
            <label htmlFor={`ress-${cnae.codigo}`} className="block text-sm font-medium text-tinta">
              Ressalva
            </label>
            <input
              id={`ress-${cnae.codigo}`}
              value={ressalva}
              onChange={(e) => setRessalva(e.target.value)}
              className="w-full rounded-cf border border-borda bg-superficie px-3 py-2 text-tinta outline-none focus:border-acento focus:ring-2 focus:ring-acento/30"
            />
            <p className="text-xs text-cinza">
              Esta sim vira AVISO laranja para o aluno. Deixe vazio quando não houver o que conferir.
            </p>
          </div>

          <div className="space-y-1.5">
            <label htmlFor={`nota-${cnae.codigo}`} className="block text-sm font-medium text-tinta">
              Nota de origem
            </label>
            <textarea
              id={`nota-${cnae.codigo}`}
              value={nota}
              rows={2}
              onChange={(e) => setNota(e.target.value)}
              className="w-full rounded-cf border border-borda bg-superficie px-3 py-2 text-sm text-tinta outline-none focus:border-acento focus:ring-2 focus:ring-acento/30"
            />
            <p className="text-xs text-cinza">
              Interna — não aparece para o aluno. Apague quando a procedência estiver resolvida.
            </p>
          </div>

          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm text-tinta">
              <input
                type="checkbox"
                checked={fatorR}
                onChange={(e) => setFatorR(e.target.checked)}
                className="size-4 accent-acento"
              />
              Sujeito ao Fator R
            </label>
            <label className="flex items-center gap-2 text-sm text-tinta">
              <input
                type="checkbox"
                checked={ativo}
                onChange={(e) => setAtivo(e.target.checked)}
                className="size-4 accent-acento"
              />
              Ativo (aparece na busca do aluno)
            </label>
          </div>

          {(cnae.fpas || cnae.rat !== null) && (
            <p className="text-xs text-cinza">
              FPAS/RAT do catálogo: {cnae.fpas ?? "—"} · RAT{" "}
              {cnae.rat === null ? "—" : emPercentual(cnae.rat, 1)}
              {cnae.fpas_rat_criterio && ` · ${cnae.fpas_rat_criterio}`}
              {cnae.nota_fpas_rat && ` · ${cnae.nota_fpas_rat}`}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3 border-t border-borda pt-3">
            <button
              type="button"
              disabled={salvando}
              onClick={() => void gravar(false)}
              className="rounded-full border border-borda px-4 py-2 text-sm font-medium text-tinta disabled:opacity-50"
            >
              {salvando ? "Salvando…" : "Salvar"}
            </button>
            {/* O carimbo é do SERVIDOR e só neste botão. Carimbar todo
                salvamento faria "conferido" significar "alguém abriu a tela" —
                a lacuna que a aba do Calendário revelou em 25/08. */}
            <button
              type="button"
              disabled={salvando}
              onClick={() => void gravar(true)}
              className="pill-cta disabled:opacity-50"
            >
              Salvar e confirmar
            </button>
            {cnae.conferido_em && (
              <span className="text-xs text-cinza">
                Conferido em {new Date(cnae.conferido_em).toLocaleDateString("pt-BR")}
                {cnae.conferido_por && ` por ${cnae.conferido_por}`}
              </span>
            )}
            {recado && (
              <span className={`text-sm ${recado.bom ? "text-aviso-tinta" : "text-erro-tinta"}`}>
                {recado.texto}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Parâmetros e faixas
// ---------------------------------------------------------------------------

function SecaoNumeros({ mostrar }: { mostrar: "parametros" | "faixas" }) {
  const [parametros, setParametros] = useState<ParametroAdmin[]>([]);
  const [faixas, setFaixas] = useState<FaixaAdmin[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);

  const buscar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await lerNumeros();
      setParametros(r.parametros);
      setFaixas(r.faixas);
      setErro(null);
    } catch (e) {
      setErro(e instanceof Error ? e.message : "não foi possível ler os números");
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void buscar();
  }, [buscar]);

  const porAnexo = useMemo(() => {
    const m = new Map<number, FaixaAdmin[]>();
    for (const f of faixas) {
      const l = m.get(f.anexo) ?? [];
      l.push(f);
      m.set(f.anexo, l);
    }
    return [...m.entries()].sort((a, b) => a[0] - b[0]);
  }, [faixas]);

  if (carregando) return <p className="text-sm text-cinza">Carregando…</p>;
  if (erro)
    return (
      <p className="rounded-cf border border-erro-borda bg-erro-fundo px-3 py-2 text-sm text-erro-tinta">
        {erro}
      </p>
    );

  if (mostrar === "parametros") {
    return (
      <div className="space-y-4">
        <section className="rounded-cf border border-borda bg-superficie p-4">
          <h3 className="text-sm font-semibold text-titulo">Os números que expiram</h3>
          <p className="mt-1 text-sm text-cinza">
            CBS e IBS são <strong>estimativa</strong> enquanto a lei complementar não sai. A
            referência aparece para o aluno junto do número — número certo sem procedência ensina a
            confiar em número velho.
          </p>
        </section>
        {parametros.map((p) => (
          <LinhaDeParametro key={p.chave} parametro={p} aoSalvar={buscar} />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <section className="rounded-cf border border-borda bg-superficie p-4">
        <h3 className="text-sm font-semibold text-titulo">As 30 faixas dos cinco anexos</h3>
        <p className="mt-1 text-sm text-cinza">
          Mudam por lei complementar. A <strong>repartição precisa somar 100%</strong> — é ela que
          divide o DAS por tributo, e uma faixa que soma 97% faz o aluno ver um DAS que não fecha com
          a soma das linhas, sem nada dizendo de onde vem a diferença.
        </p>
      </section>

      {porAnexo.map(([anexo, lista]) => (
        <section key={anexo} className="space-y-2">
          <h4 className="text-sm font-semibold text-titulo">{NOME_DO_ANEXO[anexo]}</h4>
          {lista.map((f) => (
            <LinhaDeFaixa key={`${f.anexo}-${f.faixa}`} faixa={f} aoSalvar={buscar} />
          ))}
        </section>
      ))}
    </div>
  );
}

function LinhaDeParametro({
  parametro,
  aoSalvar,
}: {
  parametro: ParametroAdmin;
  aoSalvar: () => void | Promise<void>;
}) {
  const [valor, setValor] = useState(String(parametro.valor).replace(".", ","));
  const [referencia, setReferencia] = useState(parametro.referencia);
  const [salvando, setSalvando] = useState(false);
  const [recado, setRecado] = useState<{ bom: boolean; texto: string } | null>(null);

  return (
    <div data-parametro={parametro.chave} className="rounded-cf border border-borda bg-superficie p-4">
      <h4 className="text-sm font-semibold text-titulo">{parametro.rotulo}</h4>
      <div className="mt-3 grid gap-4 sm:grid-cols-[10rem_1fr]">
        <div className="space-y-1.5">
          <label htmlFor={`v-${parametro.chave}`} className="block text-xs font-medium text-tinta">
            Alíquota
          </label>
          <div className="relative">
            <input
              id={`v-${parametro.chave}`}
              inputMode="decimal"
              value={valor}
              onChange={(e) =>
                setValor(e.target.value.replace(/[^\d,]/g, "").replace(/,(?=.*,)/g, ""))
              }
              className="w-full rounded-cf border border-borda bg-superficie px-3 py-2 pr-8 text-tinta tabular-nums outline-none focus:border-acento focus:ring-2 focus:ring-acento/30"
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-cinza">
              %
            </span>
          </div>
        </div>
        <div className="space-y-1.5">
          <label htmlFor={`r-${parametro.chave}`} className="block text-xs font-medium text-tinta">
            Referência — aparece para o aluno junto do número
          </label>
          <input
            id={`r-${parametro.chave}`}
            value={referencia}
            onChange={(e) => setReferencia(e.target.value)}
            className="w-full rounded-cf border border-borda bg-superficie px-3 py-2 text-sm text-tinta outline-none focus:border-acento focus:ring-2 focus:ring-acento/30"
          />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={salvando}
          onClick={async () => {
            setSalvando(true);
            setRecado(null);
            const n = Number(valor.replace(",", "."));
            const r = await salvarParametro(parametro.chave, n, referencia);
            setSalvando(false);
            if (r.ok) {
              setRecado({ bom: true, texto: "Salvo." });
              await aoSalvar();
            } else {
              setRecado({ bom: false, texto: mensagemDoAdmin(r) });
            }
          }}
          className="pill-cta disabled:opacity-50"
        >
          {salvando ? "Salvando…" : "Salvar"}
        </button>
        <span className="text-xs text-cinza">
          Atualizado em {new Date(parametro.atualizado_em).toLocaleDateString("pt-BR")}
        </span>
        {recado && (
          <span className={`text-sm ${recado.bom ? "text-aviso-tinta" : "text-erro-tinta"}`}>
            {recado.texto}
          </span>
        )}
      </div>
    </div>
  );
}

const TRIBUTOS_DA_FAIXA = [
  ["irpj", "IRPJ"],
  ["csll", "CSLL"],
  ["cofins", "Cofins"],
  ["pis", "PIS"],
  ["cpp", "CPP"],
  ["ipi", "IPI"],
  ["icms", "ICMS"],
  ["iss", "ISS"],
] as const;

function LinhaDeFaixa({
  faixa,
  aoSalvar,
}: {
  faixa: FaixaAdmin;
  aoSalvar: () => void | Promise<void>;
}) {
  const [aberto, setAberto] = useState(false);
  const [campos, setCampos] = useState<FaixaAdmin>(faixa);
  const [salvando, setSalvando] = useState(false);
  const [recado, setRecado] = useState<{ bom: boolean; texto: string } | null>(null);

  // A soma é calculada AQUI e no banco. As duas não são cópias da mesma regra:
  // esta existe para a equipe ver o número antes de tentar salvar, e a do banco
  // é a que recusa. Conferência não é segunda regra — é o padrão da 000071.
  const soma = TRIBUTOS_DA_FAIXA.reduce((s, [k]) => s + (campos[k] ?? 0), 0);
  const fecha = Math.abs(soma - 100) <= 0.01;

  return (
    <div
      data-faixa={`${faixa.anexo}-${faixa.faixa}`}
      className={`rounded-cf border bg-superficie ${faixa.soma && Math.abs(faixa.soma - 100) > 0.01 ? "border-erro-borda" : "border-borda"}`}
    >
      <button
        type="button"
        onClick={() => setAberto((a) => !a)}
        aria-expanded={aberto}
        className="flex w-full flex-wrap items-center gap-3 px-4 py-2.5 text-left"
      >
        <span className="text-sm font-medium text-tinta">{ordinalDaFaixa(faixa.faixa)} faixa</span>
        <span className="min-w-0 flex-1 truncate text-xs text-cinza">
          {emReais(faixa.receita_min)} a {emReais(faixa.receita_max)}
        </span>
        <span className="text-sm tabular-nums text-tinta">{emPercentual(faixa.aliquota_nominal)}</span>
        <span className="text-xs tabular-nums text-cinza">
          {faixa.parcela_deduzir > 0 ? `deduz ${emReais(faixa.parcela_deduzir)}` : "sem dedução"}
        </span>
        {Math.abs(faixa.soma - 100) > 0.01 && (
          <span className="rounded-full border border-erro-borda bg-erro-fundo px-2 py-0.5 text-[11px] font-medium text-erro-tinta">
            soma {faixa.soma.toFixed(2)}%
          </span>
        )}
        <span aria-hidden className="text-cinza">
          {aberto ? "▴" : "▾"}
        </span>
      </button>

      {aberto && (
        <div className="space-y-4 border-t border-borda px-4 py-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <CampoNumero
              id={`nom-${faixa.anexo}-${faixa.faixa}`}
              rotulo="Alíquota nominal (%)"
              valor={campos.aliquota_nominal}
              aoMudar={(n) => setCampos((c) => ({ ...c, aliquota_nominal: n }))}
            />
            <CampoNumero
              id={`ded-${faixa.anexo}-${faixa.faixa}`}
              rotulo="Parcela a deduzir (R$)"
              valor={campos.parcela_deduzir}
              aoMudar={(n) => setCampos((c) => ({ ...c, parcela_deduzir: n }))}
            />
          </div>

          <div>
            <p className="text-sm font-medium text-tinta">Repartição (%)</p>
            <div className="mt-2 grid gap-3 sm:grid-cols-4">
              {TRIBUTOS_DA_FAIXA.map(([chave, rotulo]) => (
                <CampoNumero
                  key={chave}
                  id={`${chave}-${faixa.anexo}-${faixa.faixa}`}
                  rotulo={rotulo}
                  valor={campos[chave] ?? 0}
                  aoMudar={(n) => setCampos((c) => ({ ...c, [chave]: n }))}
                />
              ))}
            </div>
            <p
              data-soma
              className={`mt-2 text-sm font-medium tabular-nums ${fecha ? "text-aviso-tinta" : "text-erro-tinta"}`}
            >
              Soma: {soma.toFixed(2)}%{fecha ? " — fecha" : " — precisa somar 100%"}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 border-t border-borda pt-3">
            <button
              type="button"
              disabled={salvando || !fecha}
              onClick={async () => {
                setSalvando(true);
                setRecado(null);
                const r = await salvarFaixa(campos);
                setSalvando(false);
                if (r.ok) {
                  setRecado({ bom: true, texto: "Salvo." });
                  await aoSalvar();
                } else {
                  setRecado({ bom: false, texto: mensagemDoAdmin(r) });
                }
              }}
              className={fecha ? "pill-cta disabled:opacity-50" : "pill-desligada"}
            >
              {salvando ? "Salvando…" : "Salvar faixa"}
            </button>
            {!fecha && (
              <span className="text-xs text-cinza">
                O botão só liga quando a repartição fecha em 100%.
              </span>
            )}
            {recado && (
              <span className={`text-sm ${recado.bom ? "text-aviso-tinta" : "text-erro-tinta"}`}>
                {recado.texto}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CampoNumero({
  id,
  rotulo,
  valor,
  aoMudar,
}: {
  id: string;
  rotulo: string;
  valor: number;
  aoMudar: (n: number) => void;
}) {
  const [texto, setTexto] = useState(String(valor).replace(".", ","));
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs font-medium text-cinza">
        {rotulo}
      </label>
      <input
        id={id}
        inputMode="decimal"
        value={texto}
        onChange={(e) => {
          const limpo = e.target.value.replace(/[^\d,]/g, "").replace(/,(?=.*,)/g, "");
          setTexto(limpo);
          const n = Number(limpo.replace(",", "."));
          aoMudar(Number.isFinite(n) ? n : 0);
        }}
        className="w-full rounded-cf border border-borda bg-superficie px-2.5 py-1.5 text-sm text-tinta tabular-nums outline-none focus:border-acento focus:ring-2 focus:ring-acento/30"
      />
    </div>
  );
}
